// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react/forbid-prop-types */
// eslint-disable-next-line import/no-unresolved
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalFooter } from './bootstrap/Modal';
import FormGroup from './bootstrap/forms/FormGroup';
import Input from './bootstrap/forms/Input';
import Select from './bootstrap/forms/Select';
import { priceFormat } from '../helpers/helpers';
import PromotionAlert from './PromotionAlert';

// import { financingAprMap } from '../../Utils/util_funcs';
import {
	INITIAL_DOWN_PAYMENT_SUBTRACTION,
	INITIAL_TERM,
	DEFAULT_MIN_LOAN_AMOUNT,
} from '../utils/constants';

const FinancingModal = ({
	financingProgram,
	financingModalOpen,
	toggleFinancingModal,
	vehiclePrice,
	promotion,
	initialVals,
	isPurchasePreorder = false,
}) => {
	const downMax =
		vehiclePrice -
		(initialVals.initialDownPaymentSubtraction ?? INITIAL_DOWN_PAYMENT_SUBTRACTION);
	const initLoanTerm = initialVals.initialLoanTerm ?? INITIAL_TERM;
	const tier1Program = financingProgram.tiers.find((tier) => tier.tierNum === 1);
	const minLoanAmount = financingProgram.minimumLoanAmount ?? DEFAULT_MIN_LOAN_AMOUNT;

	const financeTermsFormik = useFormik({
		initialValues: {
			monthlyPayment: 0,
			paymentMin: 0,
			paymentMax: 0,
			downPayment: 0,
			downMin: 0,
			downMax,
			loanTerm: initLoanTerm,
			apr: tier1Program.terms[Number(initLoanTerm)]?.newVehicleApr,
		},
	});

	// Called when monthly payment toggle is manually moved
	const monthlyPaymentUpdated = (
		newAmount,
		term = Number(financeTermsFormik.values.loanTerm),
		apr = Number(financeTermsFormik.values.apr),
		paymentMax = Number(financeTermsFormik.values.paymentMax),
		paymentMin = Number(financeTermsFormik.values.paymentMin),
	) => {
		// 1. make sure newAmount isn't bigger than max or smaller than min. If it is, make it equal max
		// 2. do calculations for downpayment
		// 3. if rounded newAmount === paymentMax, make downPayment === minDownPayment
		// 4. if rounded newAmount === paymentMin, make downPayment === maxDownPayment
		const minimumLoanAmount = vehiclePrice >= minLoanAmount ? minLoanAmount : vehiclePrice;
		const minDownpayment = 0;
		const maxDownpayment = vehiclePrice - minimumLoanAmount;

		let principal;
		if (apr === 0) {
			// With 0% APR - principal is payment * term
			principal = newAmount * term;
		} else {
			const r = apr / 12.0;
			principal = newAmount / ((r * (1.0 + r) ** term) / ((1.0 + r) ** term - 1.0));
		}

		let downPayment = vehiclePrice - principal;
		if (newAmount > paymentMax || Math.round(newAmount) === Math.round(paymentMax)) {
			newAmount = paymentMax;
			downPayment = minDownpayment;
		} else if (newAmount < paymentMin || Math.round(newAmount) === Math.round(paymentMin)) {
			newAmount = paymentMin;
			downPayment = maxDownpayment;
		}
		financeTermsFormik.setFieldValue('monthlyPayment', newAmount);
		financeTermsFormik.setFieldValue('downPayment', downPayment);
	};

	const setMonthlyPaymentRange = (term, apr) => {
		const maxLoanAmount = vehiclePrice;
		const minimumLoanAmount = vehiclePrice >= minLoanAmount ? minLoanAmount : vehiclePrice;

		let paymentMax;
		let paymentMin;

		if (apr === 0) {
			paymentMax = maxLoanAmount / term;
			paymentMin = minimumLoanAmount / term;
		} else {
			const r = apr / 12.0;
			paymentMax = (maxLoanAmount * r * (1.0 + r) ** term) / ((1.0 + r) ** term - 1.0);
			paymentMin = (minimumLoanAmount * r * (1.0 + r) ** term) / ((1 + r) ** term - 1.0);
		}

		financeTermsFormik.setFieldValue('paymentMax', paymentMax);
		financeTermsFormik.setFieldValue('paymentMin', paymentMin);
		return { paymentMax, paymentMin };
	};

	const setDownPaymentRange = async () => {
		const minimumLoanAmount = vehiclePrice >= minLoanAmount ? minLoanAmount : vehiclePrice;
		const minDownpayment = 0;
		financeTermsFormik.setFieldValue('downMin', minDownpayment);
		const maxDownpayment = vehiclePrice - minimumLoanAmount;
		financeTermsFormik.setFieldValue('downMax', maxDownpayment);
	};

	// Used on render and when payment term updates
	// 1. Sets the monthly and down payment ranges
	// 2. Sets monthlyPayment to paymentMax
	// 3. Updates downPayment accordingly
	const configureLoanTerms = (loanTerm) => {
		financeTermsFormik.setFieldValue('loanTerm', loanTerm);

		const newApr = tier1Program.terms[parseInt(loanTerm, 10)]?.newVehicleApr;
		financeTermsFormik.setFieldValue('apr', newApr);

		const { paymentMax, paymentMin } = setMonthlyPaymentRange(loanTerm, newApr);
		setDownPaymentRange();

		// Update monthly payment to max
		monthlyPaymentUpdated(paymentMax, loanTerm, newApr, paymentMax, paymentMin);
	};

	useEffect(() => {
		configureLoanTerms(initLoanTerm);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vehiclePrice]);

	// Called when down payment toggle is manually moved
	const downPaymentUpdated = async (
		newAmount,
		paymentMax = Number(financeTermsFormik.values.paymentMax),
		paymentMin = Number(financeTermsFormik.values.paymentMin),
	) => {
		// 1. make sure newAmount isn't bigger than max or smaller than min down payment. If it is, make it equal max
		// 2. do calculations for downpayment
		// 3. if rounded newAmount === downMax, make monthlyPayment === paymentMin
		// 4. if rounded newAmount === downMin, make monthlyPayment === paymentMax
		const minimumLoanAmount = vehiclePrice >= minLoanAmount ? minLoanAmount : vehiclePrice;
		const minDownpayment = 0;
		const maxDownpayment = vehiclePrice - minimumLoanAmount;
		const term = Number(financeTermsFormik.values.loanTerm);
		const newLoanAmount = vehiclePrice - newAmount;
		const apr = Number(financeTermsFormik.values.apr);

		let newMonthlyPayment;
		if (apr === 0) {
			// With 0% APR - payment is loan amount / term
			newMonthlyPayment = newLoanAmount / term;
		} else {
			const r = apr / 12.0;
			newMonthlyPayment = (newLoanAmount * r * (1.0 + r) ** term) / ((1.0 + r) ** term - 1.0);
		}

		if (newAmount > maxDownpayment || Math.round(newAmount) === Math.round(maxDownpayment)) {
			newAmount = maxDownpayment;
			newMonthlyPayment = paymentMin;
		} else if (
			newAmount < minDownpayment ||
			Math.round(newAmount) === Math.round(minDownpayment)
		) {
			newAmount = minDownpayment;
			newMonthlyPayment = paymentMax;
		}
		// set formik vals
		financeTermsFormik.setFieldValue('downPayment', newAmount);
		financeTermsFormik.setFieldValue('monthlyPayment', newMonthlyPayment);
	};

	function generateLoanTermOptions(program) {
		return Object.keys(program.terms).map((tier) => {
			return { value: Number(tier), text: `${tier} Months` };
		});
	}

	return (
		<Modal
			isOpen={financingModalOpen} // Example: state
			setIsOpen={toggleFinancingModal} // Example: setState
			isStaticBackdrop={false}
			isScrollable
			isCentered
			size='lg' // 'sm' || 'lg' || 'xl'
			fullScreen='sm' // true || 'sm' || 'md' || 'lg' || 'xl' || 'xxl'
			isAnimation={false}>
			<ModalHeader
				className='d-flex justify-content-between'
				setIsOpen={toggleFinancingModal} // Example: setState
			>
				<ModalTitle
					id='test'
					className='h3 d-flex justify-content-end'
					style={{ textAlign: 'left' }}>
					Calculate my Payment*
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<div className='col-12'>
					<p className='text-muted pb-3'>
						You will see the true terms you qualify for only after completing the
						financing application. Below are estimated terms for your reference.
					</p>
					{promotion && !isPurchasePreorder && (
						<PromotionAlert promotionMetadata={promotion} />
					)}

					<br />
					<hr />
					<div className='col-12 d-flex justify-content-between'>
						<div className='hor-stack '>
							<div className='block-top'>
								{priceFormat(Number(financeTermsFormik.values.monthlyPayment))}
							</div>
							<div className='block-bottom'>Monthly payment</div>
						</div>
						<div className='hor-stack'>
							<div className='block-top'>
								{priceFormat(Number(financeTermsFormik.values.downPayment))}
							</div>
							<div className='block-bottom'>Down payment</div>
						</div>
						<div className='hor-stack'>
							<div className='block-top'>
								{financeTermsFormik.values.apr > 0
									? `${
											Math.round(financeTermsFormik.values.apr * 100 * 100) /
											100
									  }%`
									: '0%'}
							</div>
							<div className='block-bottom'>APR</div>
						</div>
						<div className='hor-stack'>
							<div className='block-top'>
								{financeTermsFormik.values.loanTerm.toString()}
							</div>
							<div className='block-bottom'>Months</div>
						</div>
					</div>
					<hr />
				</div>
				<br />
				<div className='row g-4'>
					<div className='col-6 flex-column'>
						<FormGroup id='monthlyPayment' label='Monthly payment'>
							<Input
								id='monthlyPayment'
								className='custom-range'
								size='lg'
								type='range'
								step={0.1}
								onChange={(e) => {
									monthlyPaymentUpdated(Number(e.target.value));
								}}
								onBlur={financeTermsFormik.handleBlur}
								value={financeTermsFormik.values.monthlyPayment}
								isValid={financeTermsFormik.isValid}
								isTouched={financeTermsFormik.touched.monthlyPayment}
								invalidFeedback={financeTermsFormik.errors.monthlyPayment}
								min={financeTermsFormik.values.paymentMin}
								max={financeTermsFormik.values.paymentMax}
							/>
						</FormGroup>
						<table className='table table-borderless regularTable min-max-label'>
							<tbody>
								<tr key='payment-min-max'>
									<td className='tableLeftStyle noVerticalPadding'>
										{priceFormat(Number(financeTermsFormik.values.paymentMin))}
									</td>
									<td className='tableRightStyle noVerticalPadding'>
										{priceFormat(Number(financeTermsFormik.values.paymentMax))}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div className='col-6 flex-column'>
						<FormGroup id='downPayment' label='Down payment'>
							<Input
								id='downPayment'
								className='custom-range'
								size='lg'
								type='range'
								step={0.1}
								onChange={(e) => {
									downPaymentUpdated(Number(e.target.value));
								}}
								onBlur={financeTermsFormik.handleBlur}
								value={financeTermsFormik.values.downPayment}
								isValid={financeTermsFormik.isValid}
								isTouched={financeTermsFormik.touched.downPayment}
								invalidFeedback={financeTermsFormik.errors.downPayment}
								min={financeTermsFormik.values.downMin}
								max={financeTermsFormik.values.downMax}
							/>
						</FormGroup>
						<table className='table table-borderless regularTable min-max-label'>
							<tbody>
								<tr key='down-min-max'>
									<td className='tableLeftStyle noVerticalPadding'>
										{priceFormat(Number(financeTermsFormik.values.downMin))}
									</td>
									<td className='tableRightStyle noVerticalPadding'>
										{priceFormat(Number(financeTermsFormik.values.downMax))}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
				<br />
				<br />
				<div className='col-12 d-flex justify-content-between'>
					<div className='col-6'>
						<FormGroup id='loanTerm' label='Loan term'>
							<Select
								id='loanTerm'
								size='lg'
								name='loanTerm'
								placeholder='24 Months'
								ariaLabel='loan term length'
								// autoComplete='email'
								list={generateLoanTermOptions(tier1Program)}
								onChange={(e) => {
									configureLoanTerms(Number(e.target.value));
								}}
								onBlur={financeTermsFormik.handleBlur}
								value={financeTermsFormik.values.loanTerm}
								isValid={financeTermsFormik.isValid}
								isTouched={financeTermsFormik.touched.loanTerm}
								invalidFeedback={financeTermsFormik.errors.loanTerm}
							/>
						</FormGroup>
					</div>
				</div>
			</ModalBody>
			<ModalFooter>
				<br />
				<p className='text-muted table-section-subtitle'>
					*Subject to credit approval. Payment terms are estimates based on information
					you select (i.e. desired down payment, monthly payment, length of contract)
					prior to the submission of your financing application. Estimated payments are
					not an offer or commitment to provide credit or financing. Actual payments and
					taxes may change based on information provided in your financing application.
					NOT ALL CUSTOMERS WILL QUALIFY FOR THE LOWEST APR.
				</p>
			</ModalFooter>
		</Modal>
	);
};

FinancingModal.propTypes = {
	financingProgram: PropTypes.object.isRequired,
	financingModalOpen: PropTypes.bool.isRequired,
	toggleFinancingModal: PropTypes.func.isRequired,
	vehiclePrice: PropTypes.number.isRequired,
	promotion: PropTypes.object.isRequired,
	initialVals: PropTypes.object,
	isPurchasePreorder: PropTypes.bool,
};

FinancingModal.defaultProps = {
	initialVals: {},
	isPurchasePreorder: false,
};

export default FinancingModal;
